import { useEffect, useState } from "react";
import { api, avatarSrc } from "@/lib/api";
import WeeklyInsights from "@/components/WeeklyInsights";
import {
  ChartBar, ChatsCircle, ChatText, CheckCircle, Headset, Books,
  ChatCircleDots, Phone, CircleNotch, TrendUp, Stack, Coins, CurrencyDollar, WarningCircle,
  ThumbsUp, ThumbsDown, GraduationCap, Smiley, ShieldCheck, ShieldWarning, Prohibit, Heart, SmileyMeh, SmileySad, SmileyXEyes, Lightbulb, Sparkle, Timer, PiggyBank, Robot, ArrowUp, Star,
} from "@phosphor-icons/react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, PieChart, Pie, Cell, LineChart, Line, Legend,
} from "recharts";

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div data-testid={`analytics-kpi-${label}`} className="bg-white border border-zinc-200 rounded-xl p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{label}</span>
        <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: `${accent}1A`, color: accent }}>
          <Icon size={18} weight="bold" />
        </div>
      </div>
      <div className="mt-3 font-display font-extrabold text-3xl tracking-tight">{value}</div>
    </div>
  );
}

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [csat, setCsat] = useState(null);
  const [security, setSecurity] = useState(null);
  const [roi, setRoi] = useState(null);
  const [roiCfg, setRoiCfg] = useState(() => {
    try { return JSON.parse(localStorage.getItem("senzum_roi_cfg")) || {}; } catch { return {}; }
  });
  const minutesPer = roiCfg.minutes ?? 6;
  const hourlyRate = roiCfg.rate ?? 350;
  const currency = roiCfg.currency ?? "kr";
  const setRoiCfgVal = (k, v) => {
    const next = { ...roiCfg, [k]: v };
    setRoiCfg(next);
    localStorage.setItem("senzum_roi_cfg", JSON.stringify(next));
  };

  useEffect(() => {
    api.get("/analytics").then((r) => setData(r.data)).catch(() => setData(false));
    api.get("/usage").then((r) => setUsage(r.data)).catch(() => setUsage(false));
    api.get("/csat").then((r) => setCsat(r.data)).catch(() => setCsat(false));
    api.get("/security").then((r) => setSecurity(r.data)).catch(() => setSecurity(false));
    api.get("/roi").then((r) => setRoi(r.data)).catch(() => setRoi(false));
  }, []);

  if (data === null) {
    return <div className="p-20 flex justify-center"><CircleNotch size={28} className="animate-spin text-[#0033FF]" /></div>;
  }
  if (data === false) {
    return <div className="p-8 text-zinc-500">Kunde inte ladda analysen.</div>;
  }

  const t = data.totals;
  const channelData = [
    { name: "Chatt", value: data.channels.chat, color: "#0033FF" },
    { name: "Röst", value: data.channels.voice, color: "#7C3AED" },
  ];
  const hasChannels = data.channels.chat + data.channels.voice > 0;
  const daily = data.daily.length ? data.daily : [{ date: "—", messages: 0, conversations: 0 }];

  return (
    <div className="p-6 sm:p-8">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-[#0033FF]/10 flex items-center justify-center"><ChartBar size={22} weight="bold" className="text-[#0033FF]" /></div>
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Analys</h1>
          <p className="text-zinc-500">Insikter om hur dina AI-agenter presterar.</p>
        </div>
      </div>

      <WeeklyInsights />

      {roi && roi.total > 0 && (() => {
        const hours = (roi.ai_resolved * minutesPer) / 60;
        const money = hours * hourlyRate;
        const fmt = (n) => Math.round(n).toLocaleString("sv-SE");
        return (
          <div className="mt-6 rounded-2xl border border-[#0033FF]/20 bg-gradient-to-br from-[#0033FF] to-[#1E40AF] text-white p-6 lg:p-8 shadow-lg" data-testid="roi-section">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-display font-bold text-xl tracking-tight flex items-center gap-2"><PiggyBank size={24} weight="fill" /> Avkastning & avlastning</h2>
              <span className="text-xs text-white/70">Baserat på {fmt(roi.total)} samtal</span>
            </div>
            <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div data-testid="roi-hours">
                <div className="flex items-center gap-1.5 text-white/70 text-xs font-semibold uppercase tracking-wider"><Timer size={14} weight="bold" /> Tid sparad</div>
                <div className="font-display font-extrabold text-3xl lg:text-4xl mt-1">{fmt(hours)} h</div>
              </div>
              <div data-testid="roi-money">
                <div className="flex items-center gap-1.5 text-white/70 text-xs font-semibold uppercase tracking-wider"><PiggyBank size={14} weight="bold" /> Kostnad sparad</div>
                <div className="font-display font-extrabold text-3xl lg:text-4xl mt-1">{fmt(money)} {currency}</div>
              </div>
              <div data-testid="roi-deflection">
                <div className="flex items-center gap-1.5 text-white/70 text-xs font-semibold uppercase tracking-wider"><ArrowUp size={14} weight="bold" /> Avlastningsgrad</div>
                <div className="font-display font-extrabold text-3xl lg:text-4xl mt-1">{roi.deflection_rate}%</div>
              </div>
              <div data-testid="roi-automated">
                <div className="flex items-center gap-1.5 text-white/70 text-xs font-semibold uppercase tracking-wider"><Robot size={14} weight="bold" /> Lösta av AI</div>
                <div className="font-display font-extrabold text-3xl lg:text-4xl mt-1">{fmt(roi.ai_resolved)}</div>
              </div>
            </div>
            <div className="mt-5 pt-4 border-t border-white/15 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/80">
              <span className="font-semibold uppercase tracking-wider text-white/60">Antaganden:</span>
              <label className="flex items-center gap-1.5">Minuter per ärende
                <input data-testid="roi-minutes" type="number" min={1} max={60} value={minutesPer}
                  onChange={(e) => setRoiCfgVal("minutes", Number(e.target.value))}
                  className="w-16 bg-white/15 rounded px-2 py-1 text-white text-center outline-none focus:bg-white/25" />
              </label>
              <label className="flex items-center gap-1.5">Timkostnad
                <input data-testid="roi-rate" type="number" min={0} value={hourlyRate}
                  onChange={(e) => setRoiCfgVal("rate", Number(e.target.value))}
                  className="w-20 bg-white/15 rounded px-2 py-1 text-white text-center outline-none focus:bg-white/25" />
              </label>
              <label className="flex items-center gap-1.5">Valuta
                <select data-testid="roi-currency" value={currency}
                  onChange={(e) => setRoiCfgVal("currency", e.target.value)}
                  className="bg-white/15 rounded px-2 py-1 text-white outline-none focus:bg-white/25">
                  {["kr", "SEK", "EUR", "USD", "GBP", "NOK", "DKK"].map((c) => <option key={c} value={c} className="text-zinc-800">{c}</option>)}
                </select>
              </label>
            </div>
          </div>
        );
      })()}

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Kpi icon={ChatsCircle} label="Konversationer" value={t.conversations} accent="#0033FF" />
        <Kpi icon={ChatText} label="Meddelanden" value={t.messages} accent="#7C3AED" />
        <Kpi icon={CheckCircle} label="Lösningsgrad" value={`${t.resolution_rate}%`} accent="#10B981" />
        <Kpi icon={Stack} label="Snitt / konversation" value={t.avg_messages_per_conversation} accent="#0EA5E9" />
        <Kpi icon={Headset} label="Eskaleringar" value={t.escalations} accent="#F97316" />
        <Kpi icon={Books} label="Kunskapsposter" value={t.knowledge_items} accent="#EC4899" />
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><TrendUp size={20} weight="bold" className="text-[#0033FF]" /> Aktivitet</h3>
            <span className="text-xs font-semibold text-zinc-400">Senaste 14 dagarna</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={daily}>
              <defs>
                <linearGradient id="am" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0033FF" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#0033FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e4e4e7", fontSize: 13 }} />
              <Area type="monotone" name="Meddelanden" dataKey="messages" stroke="#0033FF" strokeWidth={2.5} fill="url(#am)" />
              <Area type="monotone" name="Konversationer" dataKey="conversations" stroke="#7C3AED" strokeWidth={2} fillOpacity={0} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-zinc-200 rounded-xl p-6">
          <h3 className="font-display font-bold text-lg mb-2">Kanalfördelning</h3>
          {hasChannels ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={channelData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={3}>
                    {channelData.map((c) => <Cell key={c.name} fill={c.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e4e4e7", fontSize: 13 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-3 space-y-2">
                {channelData.map((c) => (
                  <div key={c.name} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />{c.name}</span>
                    <span className="font-semibold">{c.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-zinc-400 text-sm">
              <ChatCircleDots size={28} weight="duotone" className="mx-auto mb-2 text-zinc-300" />
              Ingen konversationsdata än.
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 bg-white border border-zinc-200 rounded-xl p-6">
        <h3 className="font-display font-bold text-lg mb-5">Per agent</h3>
        {data.agents.length === 0 ? (
          <div className="text-center py-10 text-zinc-400">Inga agenter än.</div>
        ) : (
          <div className="overflow-x-auto scroll-thin">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-bold uppercase tracking-[0.12em] text-zinc-400 border-b border-zinc-200">
                  <th className="py-3 pr-4 font-bold">Agent</th>
                  <th className="py-3 px-4 font-bold">Kanal</th>
                  <th className="py-3 px-4 font-bold">Status</th>
                  <th className="py-3 px-4 font-bold text-right">Konversationer</th>
                  <th className="py-3 px-4 font-bold text-right">Meddelanden</th>
                  <th className="py-3 pl-4 font-bold text-right">Eskaleringar</th>
                </tr>
              </thead>
              <tbody>
                {data.agents.map((a) => {
                  const src = avatarSrc(a);
                  return (
                    <tr key={a.id} data-testid={`analytics-agent-${a.id}`} className="border-b border-zinc-100 last:border-0">
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2.5">
                          <div className="h-8 w-8 rounded-lg flex items-center justify-center overflow-hidden text-base shrink-0" style={{ background: `${a.primary_color}1A` }}>
                            {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : (a.persona_emoji || "🤖")}
                          </div>
                          <span className="font-semibold">{a.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1.5 text-zinc-600">
                          {a.type === "voice" ? <Phone size={14} weight="bold" /> : <ChatCircleDots size={14} weight="bold" />}
                          {a.type === "voice" ? "Röst" : "Chatt"}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${a.status === "live" ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>
                          {a.status === "live" ? "Live" : "Utkast"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-semibold">{a.conversations}</td>
                      <td className="py-3 px-4 text-right">{a.messages}</td>
                      <td className="py-3 pl-4 text-right">{a.escalations}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data.sentiment && data.sentiment.rated > 0 && (
        <div className="mt-8" data-testid="mood-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-display font-bold text-xl tracking-tight flex items-center gap-2">
              <Heart size={22} weight="bold" className="text-pink-500" /> Kundernas humör
            </h2>
            <span className="text-xs text-zinc-400">Känns av automatiskt i realtid · {data.sentiment.rated} samtal</span>
          </div>
          {(() => {
            const s = data.sentiment;
            const segs = [
              { key: "happy", label: "Nöjda", icon: Smiley, color: "#16A34A" },
              { key: "neutral", label: "Neutrala", icon: SmileyMeh, color: "#0EA5E9" },
              { key: "frustrated", label: "Frustrerade", icon: SmileySad, color: "#F59E0B" },
              { key: "angry", label: "Arga", icon: SmileyXEyes, color: "#EF4444" },
            ];
            const score = s.avg_score;
            const pct = score == null ? 50 : Math.round(((score + 1) / 2) * 100);
            return (
              <div className="mt-4 grid lg:grid-cols-3 gap-6">
                <div className="bg-white border border-zinc-200 rounded-xl p-6 flex flex-col items-center justify-center" data-testid="mood-score">
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-400">Genomsnittligt humör</span>
                  <div className="mt-3 font-display font-extrabold text-4xl tracking-tight" style={{ color: pct >= 60 ? "#16A34A" : pct >= 40 ? "#0EA5E9" : "#EF4444" }}>
                    {score == null ? "—" : (score > 0 ? "+" : "") + score.toFixed(2)}
                  </div>
                  <div className="mt-4 w-full h-2.5 rounded-full bg-gradient-to-r from-red-400 via-sky-300 to-green-400 relative">
                    <div className="absolute -top-1 h-4.5 w-1.5 rounded-full bg-zinc-900 shadow" style={{ left: `calc(${pct}% - 3px)`, height: "1.1rem" }} />
                  </div>
                  <div className="mt-2 flex justify-between w-full text-[10px] text-zinc-400 font-semibold"><span>Arg</span><span>Neutral</span><span>Nöjd</span></div>
                </div>
                <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-xl p-6">
                  <div className="space-y-3">
                    {segs.map((seg) => {
                      const v = s[seg.key] || 0;
                      const p = s.rated ? Math.round((v / s.rated) * 100) : 0;
                      return (
                        <div key={seg.key} data-testid={`mood-bar-${seg.key}`}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="flex items-center gap-2 font-medium text-zinc-700"><seg.icon size={17} weight="fill" style={{ color: seg.color }} />{seg.label}</span>
                            <span className="text-zinc-500 font-semibold">{v} <span className="text-zinc-300">·</span> {p}%</span>
                          </div>
                          <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${p}%`, background: seg.color }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {security && security.totals && (
        <div className="mt-8" data-testid="security-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-display font-bold text-xl tracking-tight flex items-center gap-2">
              <ShieldCheck size={22} weight="bold" className="text-[#0033FF]" /> Säkerhet & förtroende
            </h2>
            <span className="text-xs text-zinc-400">Blockerade manipulationsförsök & osäkra svar</span>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Kpi icon={ShieldCheck} label="Blockerade totalt" value={security.totals.total} accent="#0033FF" />
            <Kpi icon={Prohibit} label="Promptmanipulation" value={security.totals.injection} accent="#EF4444" />
            <Kpi icon={ShieldWarning} label="Osäkra svar" value={security.totals.groundedness} accent="#F59E0B" />
          </div>

          {security.totals.total === 0 ? (
            <div className="mt-4 bg-white border border-zinc-200 rounded-xl p-8 text-center text-sm text-zinc-500" data-testid="security-empty">
              <ShieldCheck size={28} weight="duotone" className="mx-auto mb-2 text-[#0033FF]/40" />
              Inga säkerhetshändelser ännu — dina agenter har inte blockerat något manipulationsförsök eller osäkert svar.
            </div>
          ) : (
            <div className="mt-5 grid lg:grid-cols-2 gap-6">
              <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-zinc-100 text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">Per agent</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-zinc-400 text-xs uppercase tracking-wider border-b border-zinc-100">
                      <th className="py-3 px-4 font-semibold">Agent</th>
                      <th className="py-3 px-4 font-semibold text-right">Manipulation</th>
                      <th className="py-3 px-4 font-semibold text-right">Osäkra</th>
                      <th className="py-3 pl-4 px-4 font-semibold text-right">Totalt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {security.agents.map((a) => (
                      <tr key={a.id} className="border-b border-zinc-50 last:border-0" data-testid="security-agent-row">
                        <td className="py-3 px-4 font-medium">{a.name}</td>
                        <td className="py-3 px-4 text-right text-red-500">{a.injection || 0}</td>
                        <td className="py-3 px-4 text-right text-amber-600">{a.groundedness || 0}</td>
                        <td className="py-3 pl-4 px-4 text-right font-semibold">{a.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-zinc-100 text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">Senaste händelser</div>
                <div className="divide-y divide-zinc-50 max-h-[320px] overflow-y-auto scroll-thin">
                  {security.recent.map((e, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 px-4 py-3 text-sm" data-testid="security-event-row">
                      <div className="flex items-center gap-2.5 min-w-0">
                        {e.kind === "injection"
                          ? <span className="h-7 w-7 rounded-lg bg-red-50 text-red-500 flex items-center justify-center shrink-0"><Prohibit size={15} weight="bold" /></span>
                          : <span className="h-7 w-7 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0"><ShieldWarning size={15} weight="bold" /></span>}
                        <div className="min-w-0">
                          <div className="font-medium truncate">{e.kind === "injection" ? "Manipulationsförsök blockerat" : "Osäkert svar stoppat"}</div>
                          <div className="text-xs text-zinc-400 truncate">{e.agent_name} · {e.channel === "voice" ? "Röst" : "Chatt"}</div>
                        </div>
                      </div>
                      <span className="text-xs text-zinc-400 whitespace-nowrap">{(e.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {csat && csat.totals && (
        <div className="mt-8" data-testid="csat-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-display font-bold text-xl tracking-tight flex items-center gap-2">
              <Smiley size={22} weight="bold" className="text-[#10B981]" /> Kundnöjdhet & löpande inlärning
            </h2>
            <span className="text-xs text-zinc-400">Agenterna lär sig automatiskt av 👍/👎</span>
          </div>
          <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi icon={Smiley} label="CSAT" value={csat.totals.total ? `${csat.totals.csat}%` : "—"} accent="#10B981" />
            <Kpi icon={ThumbsUp} label="Positiva" value={csat.totals.up} accent="#16A34A" />
            <Kpi icon={ThumbsDown} label="Negativa" value={csat.totals.down} accent="#EF4444" />
            <Kpi icon={GraduationCap} label="Lärdomar" value={csat.totals.learnings} accent="#7C3AED" />
          </div>
          {csat.agents.length > 0 && (
            <div className="mt-5 bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-zinc-400 text-xs uppercase tracking-wider border-b border-zinc-100">
                    <th className="py-3 px-4 font-semibold">Agent</th>
                    <th className="py-3 px-4 font-semibold text-right">Feedback</th>
                    <th className="py-3 px-4 font-semibold text-right">Positiva</th>
                    <th className="py-3 px-4 font-semibold text-right">Negativa</th>
                    <th className="py-3 pl-4 px-4 font-semibold text-right">CSAT</th>
                  </tr>
                </thead>
                <tbody>
                  {csat.agents.map((a) => (
                    <tr key={a.id} className="border-b border-zinc-50 last:border-0" data-testid="csat-agent-row">
                      <td className="py-3 px-4 font-medium">{a.name}</td>
                      <td className="py-3 px-4 text-right">{a.total}</td>
                      <td className="py-3 px-4 text-right text-green-600">{a.up}</td>
                      <td className="py-3 px-4 text-right text-red-500">{a.down}</td>
                      <td className="py-3 pl-4 px-4 text-right font-semibold">{a.csat}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {csat.survey && ((csat.survey.csat?.count || 0) > 0 || (csat.survey.nps?.count || 0) > 0) && (() => {
            const sv = csat.survey;
            const trend = (sv.daily || []).map((d) => ({ date: (d.date || "").slice(5), avg_csat: d.avg_csat, nps: d.nps }));
            return (
              <div className="mt-6" data-testid="survey-trend-section">
                <h3 className="font-display font-bold text-lg flex items-center gap-2 mb-3">
                  <Star size={18} weight="fill" className="text-amber-400" /> Betyg efter avslutad chatt
                  <span className="text-xs font-normal text-zinc-400">(CSAT & NPS)</span>
                </h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div data-testid="survey-kpi-avg" className="bg-white border border-zinc-200 rounded-xl p-5">
                    <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Snittbetyg</span>
                    <div className="mt-3 font-display font-extrabold text-3xl tracking-tight flex items-center gap-1.5">
                      {sv.csat.avg != null ? sv.csat.avg : "—"}{sv.csat.avg != null && <Star size={22} weight="fill" className="text-amber-400" />}
                    </div>
                  </div>
                  <Kpi icon={Smiley} label="Nöjda (4–5★)" value={sv.csat.pct != null ? `${sv.csat.pct}%` : "—"} accent="#10B981" />
                  <Kpi icon={Heart} label="NPS" value={sv.nps.score != null ? sv.nps.score : "—"} accent={sv.nps.score >= 0 ? "#16A34A" : "#EF4444"} />
                  <Kpi icon={ChatText} label="Svar" value={Math.max(sv.csat.count || 0, sv.nps.count || 0)} accent="#7C3AED" />
                </div>

                {trend.length > 1 && (
                  <div className="mt-5 bg-white border border-zinc-200 rounded-xl p-6" data-testid="survey-trend-chart">
                    <div className="flex items-center justify-between mb-5">
                      <h4 className="font-display font-bold text-base flex items-center gap-2"><TrendUp size={18} weight="bold" className="text-[#0033FF]" /> Trend över tid</h4>
                      <span className="text-xs font-semibold text-zinc-400">Senaste {trend.length} dagarna</span>
                    </div>
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={false} tickLine={false} />
                        <YAxis yAxisId="csat" domain={[0, 5]} tick={{ fontSize: 11, fill: "#f59e0b" }} axisLine={false} tickLine={false} width={28} />
                        <YAxis yAxisId="nps" orientation="right" domain={[-100, 100]} tick={{ fontSize: 11, fill: "#16a34a" }} axisLine={false} tickLine={false} width={36} />
                        <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e4e4e7", fontSize: 13 }} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Line yAxisId="csat" type="monotone" name="CSAT (★)" dataKey="avg_csat" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                        <Line yAxisId="nps" type="monotone" name="NPS" dataKey="nps" stroke="#16a34a" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {(sv.agents || []).length > 0 && (
                  <div className="mt-5 bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid="survey-agent-table">
                    <div className="px-4 py-3 border-b border-zinc-100 text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">Per agent</div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-zinc-400 text-xs uppercase tracking-wider border-b border-zinc-100">
                          <th className="py-3 px-4 font-semibold">Agent</th>
                          <th className="py-3 px-4 font-semibold text-right">Svar</th>
                          <th className="py-3 px-4 font-semibold text-right">Snittbetyg</th>
                          <th className="py-3 px-4 font-semibold text-right">Nöjda</th>
                          <th className="py-3 pl-4 px-4 font-semibold text-right">NPS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sv.agents.map((a) => (
                          <tr key={a.id} className="border-b border-zinc-50 last:border-0" data-testid="survey-agent-row">
                            <td className="py-3 px-4 font-medium">{a.name}</td>
                            <td className="py-3 px-4 text-right">{a.count}</td>
                            <td className="py-3 px-4 text-right font-semibold">
                              <span className="inline-flex items-center gap-1 text-amber-500">{a.avg != null ? a.avg : "—"}{a.avg != null && <Star size={13} weight="fill" />}</span>
                            </td>
                            <td className="py-3 px-4 text-right text-green-600">{a.pct != null ? `${a.pct}%` : "—"}</td>
                            <td className={`py-3 pl-4 px-4 text-right font-semibold ${a.nps >= 0 ? "text-emerald-600" : "text-red-500"}`}>{a.nps != null ? a.nps : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {(sv.comments || []).length > 0 && (
                  <div className="mt-5 bg-white border border-zinc-200 rounded-xl p-5" data-testid="survey-comments">
                    <div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400 mb-3">Senaste kommentarer</div>
                    <div className="space-y-2.5">
                      {sv.comments.slice(0, 6).map((cm, i) => (
                        <div key={i} className="rounded-lg border border-zinc-200 px-3.5 py-2.5">
                          <div className="flex items-center gap-2 text-xs">
                            {cm.csat != null && <span className="inline-flex items-center gap-0.5 text-amber-500 font-semibold">{cm.csat}<Star size={12} weight="fill" /></span>}
                            {cm.nps != null && <span className="font-semibold text-zinc-400">NPS {cm.nps}</span>}
                            <span className="text-zinc-300">·</span>
                            <span className="text-zinc-400">{cm.agent_name}</span>
                          </div>
                          <p className="text-sm text-zinc-700 mt-1">{cm.comment}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {usage && usage.totals && (
        <div className="mt-8" data-testid="usage-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-display font-bold text-xl tracking-tight">Tokenförbrukning & kostnad</h2>
            <span className="text-xs text-zinc-400">Uppskattade värden för kostnadskontroll</span>
          </div>

          {usage.alerts && usage.alerts.length > 0 && (
            <div data-testid="usage-alert" className="mt-3 flex items-start gap-2 text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm">
              <WarningCircle size={18} weight="bold" className="shrink-0 mt-0.5" />
              <span>
                <b>Hög förbrukning:</b> {usage.alerts.map((a) => a.name).join(", ")} står för en stor del av token-användningen. Överväg kortare svar eller en billigare modell för att sänka API-kostnaden.
              </span>
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 lg:grid-cols-3 gap-4">
            <Kpi icon={Coins} label="Tokens totalt" value={usage.totals.tokens.toLocaleString("sv-SE")} accent="#0033FF" />
            <Kpi icon={CurrencyDollar} label="Uppskattad kostnad" value={`${(usage.totals.cost || 0).toLocaleString("sv-SE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`} accent="#16A34A" />
            <Kpi icon={ChatText} label="AI-anrop" value={usage.totals.requests} accent="#7C3AED" />
          </div>

          {usage.agents.length > 0 && (
            <div className="mt-5 bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-zinc-400 text-xs uppercase tracking-wider border-b border-zinc-100">
                    <th className="py-3 px-4 font-semibold">Agent</th>
                    <th className="py-3 px-4 font-semibold">Typ</th>
                    <th className="py-3 px-4 font-semibold text-right">Tokens</th>
                    <th className="py-3 px-4 font-semibold text-right">Andel</th>
                    <th className="py-3 pl-4 px-4 font-semibold text-right">Kostnad</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.agents.map((a) => (
                    <tr key={a.id} className="border-b border-zinc-50 last:border-0" data-testid="usage-agent-row">
                      <td className="py-3 px-4 font-medium flex items-center gap-2">
                        {a.high_usage && <WarningCircle size={15} weight="fill" className="text-amber-500" title="Hög förbrukning" />}
                        {a.name}
                      </td>
                      <td className="py-3 px-4 text-zinc-500 capitalize">{a.type}</td>
                      <td className="py-3 px-4 text-right">{a.tokens.toLocaleString("sv-SE")}</td>
                      <td className="py-3 px-4 text-right">{a.share}%</td>
                      <td className="py-3 pl-4 px-4 text-right font-semibold">{`${(a.cost || 0).toLocaleString("sv-SE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
